import { useEffect, useMemo, useRef, useState } from 'react';
import {
	useModeStore,
	useAutonomousKartStore,
	useMissionStore,
} from '../../store/sceneStore';
import { useSendUserInput } from '../../provider/socketContext';

import { CruiseButton } from './CruiseButton';
import { ParkingButton } from './ParkingButton';
import { TargetSpeed } from './TargetSpeed';
import { CurrentSpeed } from './CurrentSpeed';
import { AutonomousStop } from '../../components/autonomousStop';
import { CruiseStartButton } from './CruiseStartButton';

// --- MAIN APP COMPONENT ---
export function CartMain() {
	const { ready, run } = useModeStore();
	const { parkingAvailable } = useAutonomousKartStore();
	const { subMission } = useMissionStore();
	const sendUserInput = useSendUserInput();
	const isParking = useMemo(() => subMission === 'parking', [subMission]);

	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const [isButtonClicked, setIsButtonClicked] = useState(false);

	const handleCruiseStart = () => {
		const msg = {
			type: 'user_input',
			text: 'ready_button',
			value: 1,
		};
		sendUserInput(msg);
	};

	const handleCruiseStartWithCancel = () => {
		setIsButtonClicked(true);
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
		handleCruiseStart();
	};

	const handleDriving = () => {
		const msg = {
			type: 'user_input',
			text: 'run_button',
			value: 1,
		};
		sendUserInput(msg);
	};

	const handleParking = (value: boolean) => {
		sendUserInput({
			type: 'user_input',
			text: 'run_button',
			value: 1,
		});

		sendUserInput({
			type: 'user_input',
			text: 'parking_request',
			value,
		});
	};

	useEffect(() => {
		if (!ready && !isButtonClicked) {
			timeoutRef.current = setTimeout(() => {
				handleCruiseStart();
			}, 3000);
		}

		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	return (
		<div className='bg-[#0f2027] w-screen h-screen flex justify-center items-center p-6 text-white overflow-hidden'>
			<main className='w-full h-full flex flex-col gap-6 max-w-7xl mx-auto'>
				{/* Top Section: Status & Speed Info */}
				<div className='flex-1 grid grid-cols-2 gap-6 min-h-0'>
					{/* Current Speed Panel */}
					<section className='bg-[#1a2e36] rounded-3xl p-8 flex flex-col items-center justify-center shadow-2xl ring-1 ring-white/5 relative overflow-hidden'>
						<div className='absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none' />
						<CurrentSpeed />
					</section>

					{/* Target Speed Panel */}
					<section
						className={`bg-[#1a2e36] rounded-3xl p-8 flex flex-col items-center justify-center shadow-2xl ring-1 ring-white/5 relative overflow-hidden transition-all duration-500 ${!ready ? 'opacity-50 grayscale' : ''}`}
					>
						<div className='absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none' />
						{ready ? (
							<TargetSpeed disabled={isParking} />
						) : (
							<div className='flex flex-col items-center justify-center text-zinc-500 h-full'>
								<span className='text-4xl font-bold'>주행 준비 중</span>
							</div>
						)}
					</section>
				</div>

				{/* Bottom Section: Controls */}
				<div className='h-[200px] w-full shrink-0'>
					{ready ? (
						<div className='flex gap-6 h-full'>
							<div className='flex-[2] h-full'>
								<CruiseButton
									onClick={handleDriving}
									isDriving={run}
									disabled={isParking}
								/>
							</div>
							<div className='flex-1 h-full'>
								<ParkingButton
									onClick={handleParking}
									isParking={isParking}
									disabled={!parkingAvailable}
								/>
							</div>
						</div>
					) : (
						<div className='w-full h-full'>
							<CruiseStartButton onClick={handleCruiseStartWithCancel} />
						</div>
					)}
				</div>
			</main>
			<AutonomousStop />
		</div>
	);
}
